import React, { useMemo } from 'react';
import { BlurView } from 'expo-blur';
import { defineMessages, useIntl } from 'react-intl';
import { Platform, StyleSheet, View } from 'react-native';
import { useStyle } from 'react-native-style-utilities';

import { theme } from '@garnish/constants';
import { ButtonCenterLocation, ButtonZoom, useResponsive } from '@garnish/core';
import { GlassView } from '@garnish/glass';

// ────────────────────────────────────────────────────────────────────────────────

export const MapControls = (props: Props) => {
  const {
    onZoomInPress,
    onZoomOutPress,
    onGeolocateUserBtnPress,
    disableGeolocateUserBtn,
    controlsRightInset,
    controlsTopInset,
    withBlurredControls,
    isLiquidGlassEnabled,
  } = props;

  const { formatMessage } = useIntl();
  const { match } = useResponsive();

  // ─────────────────────────────────────────────────────────────────────────────

  const zoomInLabel = formatMessage(messages.zoomInLabel);
  const zoomOutLabel = formatMessage(messages.zoomOutLabel);
  const geolocateUserBtnLabel = formatMessage(messages.geolocateUserBtnLabel);

  // ─────────────────────────────────────────────────────────────────────────────

  const zoomProps = useMemo(
    () => ({
      zoomInProps: { onPress: onZoomInPress, accessibilityLabel: zoomInLabel },
      zoomOutProps: { onPress: onZoomOutPress, accessibilityLabel: zoomOutLabel }, // prettier-ignore
    }),
    [onZoomInPress, onZoomOutPress, zoomInLabel, zoomOutLabel],
  );

  // ─── Styles ──────────────────────────────────────────────────────────────────

  const controlsInsetStyles = useStyle(() => {
    if (controlsRightInset === undefined && controlsTopInset === undefined) return;

    return {
      ...(controlsRightInset === undefined ? {} : { right: controlsRightInset }),
      ...(controlsTopInset === undefined ? {} : { top: controlsTopInset }),
    };
  }, [controlsRightInset, controlsTopInset]);

  const controlsContainerStyles = [
    styles.controlsContainer,
    match([styles.controlsContainerXS, styles.controlsContainerSM]),
    controlsInsetStyles,
  ];
  const controlStyles = isLiquidGlassEnabled ? styles.controlGlass : styles.controlBlur;

  // ─────────────────────────────────────────────────────────────────────

  if (!withBlurredControls) {
    return (
      <View style={controlsContainerStyles}>
        <ButtonCenterLocation
          onPress={onGeolocateUserBtnPress}
          disabled={disableGeolocateUserBtn}
          accessibilityLabel={geolocateUserBtnLabel}
        />

        <ButtonZoom zoomInProps={zoomProps.zoomInProps} zoomOutProps={zoomProps.zoomOutProps} />
      </View>
    );
  }

  return (
    <View style={controlsContainerStyles}>
      <View style={[controlStyles, styles.centerLocationControl]}>
        <View
          style={[styles.controlBackground, styles.centerLocationBackground]}
          pointerEvents="none"
        >
          {isLiquidGlassEnabled ? (
            <GlassView
              glassEffectStyle="regular"
              tintColor={GLASS_TINT_COLOR}
              style={[StyleSheet.absoluteFill, styles.centerLocationBackground]}
            />
          ) : (
            <BlurView
              intensity={CONTROL_BLUR_INTENSITY}
              tint="light"
              style={[StyleSheet.absoluteFill, styles.centerLocationBackground]}
            />
          )}
        </View>

        <ButtonCenterLocation
          onPress={onGeolocateUserBtnPress}
          disabled={disableGeolocateUserBtn}
          accessibilityLabel={geolocateUserBtnLabel}
          withTransparentBackground
        />
      </View>

      <View style={[controlStyles, styles.zoomControl]}>
        <View style={[styles.controlBackground, styles.zoomBackground]} pointerEvents="none">
          {isLiquidGlassEnabled ? (
            <GlassView
              glassEffectStyle="regular"
              tintColor={GLASS_TINT_COLOR}
              style={[StyleSheet.absoluteFill, styles.zoomBackground]}
            />
          ) : (
            <BlurView
              intensity={CONTROL_BLUR_INTENSITY}
              tint="light"
              style={[StyleSheet.absoluteFill, styles.zoomBackground]}
            />
          )}
        </View>

        <ButtonZoom
          zoomInProps={zoomProps.zoomInProps}
          zoomOutProps={zoomProps.zoomOutProps}
          withTransparentBackground
        />
      </View>
    </View>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const CONTROL_BLUR_INTENSITY = 85;
const GLASS_TINT_COLOR = 'rgba(255, 255, 255, 0.3)';

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  geolocateUserBtnLabel: {
    defaultMessage: 'Get current location and center map on it',
    description: 'Map Controls | Geolocate User Button | Accessibility Label',
  },
  zoomInLabel: {
    defaultMessage: 'Zoom in',
    description: 'Map Controls | Zoom In Button | Accessibility Label',
  },
  zoomOutLabel: {
    defaultMessage: 'Zoom out',
    description: 'Map Controls | Zoom Out Button | Accessibility Label',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  controlsContainer: {
    position: 'absolute',
    zIndex: 1,
    gap: theme.spacing['4'],
  },
  controlsContainerXS: {
    top: theme.spacing['4'],
    right: theme.spacing['4'],
  },
  controlsContainerSM: {
    right: theme.spacing['6'],
    bottom: theme.spacing['6'],
  },
  controlBackground: {
    ...StyleSheet.absoluteFill,
    overflow: 'hidden',
  },
  controlBlur: {
    backgroundColor: Platform.select({
      android: 'rgba(250, 250, 246, 0.95)',
      default: 'rgba(250, 250, 246, 0.7)',
    }),
    ...theme.elevations['4'],
  },
  controlGlass: {
    ...theme.elevations['4'],
  },
  centerLocationControl: {
    borderRadius: 40,
  },
  centerLocationBackground: {
    borderRadius: 40,
    overflow: 'hidden',
  },
  zoomControl: {
    borderRadius: theme.radius.medium,
  },
  zoomBackground: {
    borderRadius: theme.radius.medium,
    overflow: 'hidden',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

export type Props = {
  onZoomInPress?: () => void;
  onZoomOutPress?: () => void;
  onGeolocateUserBtnPress?: () => void;
  disableGeolocateUserBtn?: boolean;
  controlsRightInset?: number;
  controlsTopInset?: number;
  withBlurredControls?: boolean;
  isLiquidGlassEnabled?: boolean;
};
